import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { MenuItem, Select, SelectChangeEvent, SelectProps } from '@mui/material';
import { useState } from 'react';

import { DEFAULT_PLACEHOLDER_FIELD_LABEL } from '@/pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/util';
import { MOUSE_EVENT_BUTTONS } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/const';
import { createTemplatePlaceholder } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/util';
import TemplatePlaceholderContextMenu, {
  CloseOutcome,
  ContextMenuState,
  handleOpenPlaceholderContextMenu,
} from './TemplatePlaceholderContextMenu';
import { PLACEHOLDER_BLUE, PLACEHOLDER_BLUE_BORDER } from './colours';

export type TemplateCapableSelectProps = Omit<SelectProps, 'value' | 'onChange'> & {
  value: string | TemplatePlaceholder;
  onChange: (
    newValue: string | TemplatePlaceholder,
    reason: 'normal' | 'setIsTemplatePlaceholder' | 'unsetIsTemplatePlaceholder'
  ) => void;
  isError?: boolean;
  isOnTemplatePage?: boolean;
  templateBuilderDisplayName?: string;

  /** This is put in the actual placeholder prop of the underlying htmlInput
   * when the value is a placeholder. Defaults to ''Placeholder field''*/
  templateBuilderPlaceholderFieldLabel?: string;

  /**
   * When the component is swithed from a placeholder value to a normal value,
   * it will be reset to this. We can't easily track it in the
   * TemplateCapableSelect itself due the component sometimes being re-rendered
   * within the query builder scenarios.
   */
  valueAfterRemovePlaceholder: string;

  'data-testid'?: string;
};

export default function TemplateCapableSelect({
  value,
  onChange,
  templateBuilderDisplayName = '',
  isOnTemplatePage = false,
  disabled = false,
  sx = {},
  inputProps = {},
  templateBuilderPlaceholderFieldLabel = DEFAULT_PLACEHOLDER_FIELD_LABEL,
  valueAfterRemovePlaceholder,
  children,
  ...props
}: TemplateCapableSelectProps) {
  const [placeholderMenuState, setPlaceholderMenuState] = useState<ContextMenuState>(null);

  const valueIsPlaceholder = isTemplatePlaceholder(value);

  const conditionalPlaceholderProps: Partial<SelectProps> = valueIsPlaceholder
    ? {
        // The following gives it the placeholder styling
        color: 'info',
        IconComponent: () => null,
        sx: {
          ...sx,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent', // Stops it turning grey on hover
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent', // Stops it turning grey when clicked
          },

          // Background and text color for both outlined and filled variants
          '& .MuiSelect-select': {
            backgroundColor: PLACEHOLDER_BLUE,
            color: 'rgba(0, 0, 0, 0.3654)',
          },
          // Blue border for outlined variant
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: PLACEHOLDER_BLUE_BORDER,
          },
          // Blue underline for filled variant (simulates focused state)
          '&.MuiFilledInput-root::after': {
            borderBottomColor: PLACEHOLDER_BLUE_BORDER,
            transform: 'scaleX(1)',
          },
        },
        inputProps: {
          ...inputProps,
          placeholder: templateBuilderPlaceholderFieldLabel,
          readOnly: true,
        },
        displayEmpty: true,
      }
    : {
        inputProps,
        sx,
      };

  const handleCloseTemplatePlaceholderContextMenu = (reason: CloseOutcome) => {
    setPlaceholderMenuState(null);
    if (reason === 'setIsTemplatePlaceholder') {
      onChange(createTemplatePlaceholder(templateBuilderDisplayName), 'setIsTemplatePlaceholder');
    } else if (reason === 'unsetIsTemplatePlaceholder') {
      onChange(valueAfterRemovePlaceholder, 'unsetIsTemplatePlaceholder');
    }
  };

  const handleNormalOnChange = (event: SelectChangeEvent<unknown>) => {
    onChange(event.target.value as string, 'normal');
  };

  if (isOnTemplatePage) {
    return (
      <>
        <Select
          value={valueIsPlaceholder ? '' : value}
          onChange={handleNormalOnChange}
          onMouseDownCapture={(evt: React.MouseEvent) => {
            if (evt.button === MOUSE_EVENT_BUTTONS.right) {
              evt.preventDefault();
              evt.stopPropagation();
            }
          }}
          onContextMenuCapture={(evt: React.MouseEvent) => {
            evt.preventDefault();
            evt.stopPropagation();

            if (!disabled) {
              handleOpenPlaceholderContextMenu(evt, placeholderMenuState, setPlaceholderMenuState);
            }
          }}
          {...conditionalPlaceholderProps}
          disabled={disabled}
          {...props}
        >
          {valueIsPlaceholder ? (
            <MenuItem value="">{templateBuilderPlaceholderFieldLabel}</MenuItem>
          ) : (
            children
          )}
        </Select>
        <TemplatePlaceholderContextMenu
          menuState={placeholderMenuState}
          placeholderActive={valueIsPlaceholder}
          onClose={handleCloseTemplatePlaceholderContextMenu}
        />
      </>
    );
  }

  if (valueIsPlaceholder) {
    throw new Error('TemplateCapableSelect: value is a placeholder but isOnTemplatePage is false');
  }

  return (
    <Select value={value} onChange={handleNormalOnChange} disabled={disabled} sx={sx} {...props}>
      {children}
    </Select>
  );
}
